import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { placementSidesSignature } from '@/lib/photos/placed-pages'
import { scheduleAlbumPhotosRefresh } from '@/lib/photos/schedule-album-photos-refresh'
import type { PageSide } from '@/lib/photos/types/page-side'

/**
 * Re-fetches a photo's details (debounced) after this session changes the pages it is on, so the
 * server's page list catches up with the placements laid over it, as `usePortraitPlacementRefresh`
 * does for the portrait roster. Only this photo's own pages count: another photo's placement, or
 * selecting a different photo, is not a change.
 */
export function usePhotoDetailPlacementRefresh(
  imageId: string | null,
  isPortraitSource: boolean,
  sidesBySpread: ReadonlyMap<string, ReadonlySet<PageSide>> | undefined
) {
  const queryClient = useQueryClient()
  const signature = placementSidesSignature(sidesBySpread)
  const seenRef = useRef({ imageId, signature })

  useEffect(() => {
    const seen = seenRef.current

    seenRef.current = { imageId, signature }

    if (!imageId || seen.imageId !== imageId || seen.signature === signature) return

    scheduleAlbumPhotosRefresh(
      queryClient,
      isPortraitSource
        ? queryKeys.portraits.detail(imageId)
        : queryKeys.photos.candidDetail(imageId)
    )
  }, [imageId, isPortraitSource, signature, queryClient])
}
